import React, { useState } from 'react';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

/**
 * 필터 버튼 하나의 설정
 * @typedef {Object} FilterItem
 * @property {string} key - 필터 식별자 (페이지 내 유일)
 * @property {string} label - 필터 이름 (예: '부서', '공정', '년도', '월')
 * @property {string} [value] - 현재 선택값 표시 (예: '전체', '2024', '3월')
 */

/**
 * 페이지별로 다른 필터 목록을 좌우 스크롤 가능한 바로 표시하는 공통 컴포넌트
 * @param {Object} props
 * @param {FilterItem[]} props.items - 필터 항목 배열 (페이지마다 구성 가능)
 * @param {Object.<string, Array<{ value: string, label: string }>>} [props.optionsByKey] - 필터별 옵션 목록 (제공되면 내부 selectBox UI 사용)
 * @param {function(string, { value: string, label: string }): void} [props.onSelect] - 옵션 선택 시 콜백 (key, option)
 * @param {string} [props.className] - 컨테이너 추가 클래스
 * @param {string} [props.buttonClassName] - 버튼 공통 추가 클래스
 */
function FilterBar(props) {
  const {
    items = [],
    optionsByKey = {},
    onSelect,
    className = 'bg-white py-2.5',
    isPadding = false,
    buttonClassName = '',
  } = props;
  const innerPadding = isPadding ? 'px-4' : '';
  const [openKey, setOpenKey] = useState(null);

  const handleClick = (key) => {
    if (optionsByKey[key] && Array.isArray(optionsByKey[key]) && onSelect) {
      setOpenKey((prev) => (prev === key ? null : key));
      return;
    }
  };

  const handleOptionClick = (key, option) => {
    if (onSelect) {
      onSelect(key, option);
    }
    setOpenKey(null);
  };

  if (items.length === 0) return null;

  return (
    <div
      className={`flex-shrink-0 border-b border-slate-100 ${className}`.trim()}
    >
      <div className="flex flex-col gap-1">
        <div
          className={innerPadding}
          style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <div
            className="flex flex-nowrap gap-2"
            style={{ width: 'max-content' }}
          >
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => handleClick(item.key)}
                className={`relative flex flex-shrink-0 items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600 active:bg-slate-100 ${buttonClassName}`.trim()}
              >
                <span>
                  {item.label}: {item.value ?? '전체'}
                </span>
                <ExpandMoreIcon className="!text-base" />
              </button>
            ))}
          </div>
        </div>

        {openKey &&
          optionsByKey[openKey] &&
          optionsByKey[openKey].length > 0 && (
            <div className="px-4 pt-1">
              <div className="max-h-48 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-md">
                {optionsByKey[openKey].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleOptionClick(openKey, opt)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50 active:bg-slate-100"
                  >
                    <span>{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
      </div>
    </div>
  );
}

export default FilterBar;
